import { create } from 'zustand';
import { getApiService } from '@infra/api/api-service.ts';
import { tokenStorage } from '@infra/storage/token-storage.ts';
import type { CompaniesOutputDTO, UsersOutputDTO } from '@core/types/auth.types.ts';

interface AuthState {
  isAuthenticated: boolean;
  user: UsersOutputDTO | null;
  company: CompaniesOutputDTO | null;
  isLoading: boolean;
  error: string | null;

  login: (credentials: { username: string; password: string }) => Promise<void>;
  logout: () => void;
  hydrate: () => void;
}

export const useAuthStore = create<AuthState>()((set) => ({
  isAuthenticated: false,
  user: null,
  company: null,
  isLoading: false,
  error: null,

  hydrate() {
    const hasToken = !!tokenStorage.getAccessToken() && !tokenStorage.isTokenExpired();
    set({
      isAuthenticated: hasToken,
      user: hasToken ? tokenStorage.getUserData() : null,
      company: hasToken ? tokenStorage.getCompanyData() : null,
    });
  },

  async login({ username, password }) {
    set({ isLoading: true, error: null });
    try {
      const data = await getApiService().login({ username, password });
      tokenStorage.setUserData(data.user_default);
      tokenStorage.setCompanyData(data.company);
      set({
        isAuthenticated: true,
        user: data.user_default,
        company: data.company,
        isLoading: false,
      });
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Error de autenticación';
      set({ isLoading: false, error: message });
      throw err;
    }
  },

  logout() {
    tokenStorage.clearTokens();
    set({ isAuthenticated: false, user: null, company: null, error: null });
  },
}));
